import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PIPELINE_STAGES, STAGE_LABELS, STAGE_COLORS, PipelineStage } from '@/types';
import { cn } from '@/lib/utils';

export interface PipelineStageCount {
  stage: PipelineStage;
  count: number;
}

interface PipelineOverviewProps {
  data: PipelineStageCount[] | null;
  isLoading: boolean;
}

// Only show active pipeline stages (not disqualified)
const ACTIVE_STAGES = PIPELINE_STAGES.filter((s) => s !== 'disqualified');

export function PipelineOverview({ data, isLoading }: PipelineOverviewProps) {
  if (isLoading) {
    return (
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-lg">Pipeline Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {ACTIVE_STAGES.map((stage) => (
              <div key={stage} className="space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-8" />
                </div>
                <Skeleton className="h-6 w-full rounded-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Build a map of stage to count for easy lookup
  const stageCountMap = new Map<PipelineStage, number>();
  if (data) {
    data.forEach((item) => {
      stageCountMap.set(item.stage, item.count);
    });
  }

  // Calculate max count for bar scaling
  const maxCount = Math.max(
    ...ACTIVE_STAGES.map((stage) => stageCountMap.get(stage) || 0),
    1, // Prevent division by zero
  );

  // Calculate total for percentage display
  const total = ACTIVE_STAGES.reduce(
    (sum, stage) => sum + (stageCountMap.get(stage) || 0),
    0,
  );

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Pipeline Overview</CardTitle>
          <span className="text-sm text-text-tertiary">
            {total.toLocaleString()} total applications
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <div className="h-64 flex items-center justify-center text-text-tertiary">
            No pipeline data available
          </div>
        ) : (
          <div className="space-y-3">
            {ACTIVE_STAGES.map((stage) => {
              const count = stageCountMap.get(stage) || 0;
              const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
              const bgColor = STAGE_COLORS[stage];

              return (
                <div key={stage} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary font-medium">
                      {STAGE_LABELS[stage]}
                    </span>
                    <span className="text-text-tertiary tabular-nums">
                      {count.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-5 w-full rounded-full bg-black/40 border border-white/10 shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)] overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-500',
                        'bg-gradient-to-b from-white/30 via-white/5 to-black/20 bg-blend-overlay',
                        'shadow-[0_1px_3px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.35)]',
                        bgColor,
                      )}
                      style={{ width: `${Math.max(percentage, count > 0 ? 2 : 0)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
